import React, { useState, useEffect, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import { useStore } from "../../../../stores/store";
import { useSetMenu } from "../../../../hooks/useSetMenu";
import FormLayoutSelection from "../../../Generic/FormLayoutSelection";
import FormSelectSelection from "../../../Generic/FormSelectSelection";
import CriterionValueGroup from "../../../Generic/CriterionValueGroup";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ScienceIcon from "@mui/icons-material/Science";
import BarChartIcon from "@mui/icons-material/BarChart";
import TableChartIcon from "@mui/icons-material/TableChart";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LinearProgress from "@mui/material/LinearProgress";

const MethodComparison = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();
  const { mainStore } = useStore();
  useSetMenu(mainStore.ResearchMenuType, mainStore.MethodComparisonMenuNo);

  // State for selections
  const [panelOptions, setPanelOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [toolsInSubcategory, setToolsInSubcategory] = useState([]);

  // Criteria groups
  const [criteriaW1, setCriteriaW1] = useState([]);
  const [criteriaW05, setCriteriaW05] = useState([]);
  const [criteriaW033, setCriteriaW033] = useState([]);
  const [selectedCVIdsW1, setSelectedCVIdsW1] = useState([]);
  const [selectedCVIdsW05, setSelectedCVIdsW05] = useState([]);
  const [selectedCVIdsW033, setSelectedCVIdsW033] = useState([]);

  // Loading states
  const [loadingCriteria, setLoadingCriteria] = useState(false);
  const [loadingComparison, setLoadingComparison] = useState(false);

  // Comparison results
  const [comparisonResult, setComparisonResult] = useState(null);
  const [hasCompared, setHasCompared] = useState(false);

  // Load panels on mount
  useEffect(() => {
    const loadPanels = async () => {
      try {
        const res = await axiosPrivate.get("/Panels/SelectOptions");
        setPanelOptions(res.data);
      } catch (err) {
        enqueueSnackbar(t("errorMessageError"), { variant: "error" });
      }
    };
    loadPanels();
  }, [axiosPrivate, enqueueSnackbar, t]);

  // Reset fields helper
  const resetFields = () => {
    setHasCompared(false);
    setComparisonResult(null);
    setCriteriaW1([]);
    setCriteriaW05([]);
    setCriteriaW033([]);
    setSelectedCVIdsW1([]);
    setSelectedCVIdsW05([]);
    setSelectedCVIdsW033([]);
  };

  // Cascading dropdown handlers
  const handlePanelChange = async (option) => {
    setSelectedPanel(option);
    setSelectedCategory(null);
    setCategoryOptions([]);
    setSelectedSubcategory(null);
    setSubcategoryOptions([]);
    setToolsInSubcategory([]);
    resetFields();

    if (!option) return;
    try {
      const catRes = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${option.value}`);
      setCategoryOptions(Array.isArray(catRes.data) ? catRes.data : []);
    } catch (error) {
      enqueueSnackbar(t("errorMessageError"), { variant: "error" });
    }
  };

  const handleCategoryChange = async (option) => {
    setSelectedCategory(option);
    setSelectedSubcategory(null);
    setSubcategoryOptions([]);
    setToolsInSubcategory([]);
    resetFields();

    if (!option) return;
    try {
      const subRes = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${option.value}`);
      setSubcategoryOptions(Array.isArray(subRes.data) ? subRes.data : []);
    } catch (err) {
      enqueueSnackbar(t("errorMessageError"), { variant: "error" });
    }
  };

  const handleSubcategoryChange = async (option) => {
    setSelectedSubcategory(option);
    setToolsInSubcategory([]);
    resetFields();

    if (!option) return;

    setLoadingCriteria(true);
    try {
      const toolsRes = await axiosPrivate.get(`/ToolPassports/SelectOptionsBySubcategory/${option.value}`);
      setToolsInSubcategory(Array.isArray(toolsRes.data) ? toolsRes.data : []);

      const cRes = await axiosPrivate.get(`/Criterias/WithValuesBySubcategory/${option.value}`);
      const allCriteria = Array.isArray(cRes.data) ? cRes.data : [];
      setCriteriaW1(allCriteria.filter((c) => c.weightCoefficient === 1.0));
      setCriteriaW05(allCriteria.filter((c) => c.weightCoefficient === 0.5));
      setCriteriaW033(allCriteria.filter((c) => c.weightCoefficient === 0.33));
    } catch (err) {
      enqueueSnackbar(t("errorMessageError"), { variant: "error" });
    } finally {
      setLoadingCriteria(false);
    }
  };

  // Compare all methods
  const handleCompare = async () => {
    if (!selectedSubcategory) {
      enqueueSnackbar(t("selectRequired"), { variant: "error" });
      return;
    }

    // Build criteria selection
    const chosenCVIds = [...selectedCVIdsW1, ...selectedCVIdsW05, ...selectedCVIdsW033];

    const cvMap = {};
    const fillMap = (criteriaList) => {
      criteriaList.forEach((crit) => {
        crit.values.forEach((cv) => {
          cvMap[cv.id] = {
            criterionId: crit.id,
            weight: crit.weightCoefficient,
          };
        });
      });
    };
    fillMap(criteriaW1);
    fillMap(criteriaW05);
    fillMap(criteriaW033);

    const selectedCriteriaArray = [];
    const weightsMap = {};

    chosenCVIds.forEach((cvIdStr) => {
      const cvId = parseInt(cvIdStr, 10);
      const info = cvMap[cvId];
      if (info) {
        selectedCriteriaArray.push({
          criteriaId: info.criterionId,
          value: cvId,
          weight: info.weight,
        });
        weightsMap[info.criterionId] = info.weight;
      }
    });

    const criteriaWeightsArray = Object.entries(weightsMap).map(([critIdStr, w]) => ({
      criteriaId: parseInt(critIdStr, 10),
      weight: w,
    }));

    const finalTools = toolsInSubcategory.map((tool) => ({
      toolId: parseInt(tool.value, 10),
    }));

    const decisionRequest = {
      Method: "ALL", // Will be handled by compare-methods endpoint
      SubcategoryId: parseInt(selectedSubcategory.value, 10),
      SelectedCriteria: selectedCriteriaArray,
      SelectedTools: finalTools,
      CriteriaWeights: criteriaWeightsArray,
    };

    setLoadingComparison(true);
    try {
      const response = await axiosPrivate.post("/DecisionMaking/compare-methods", decisionRequest);
      if (response.data) {
        setComparisonResult(response.data);
        setHasCompared(true);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t("errorMessageError"), { variant: "error" });
    } finally {
      setLoadingComparison(false);
    }
  };

  // Method colors for visual distinction
  const methodColors = {
    MAUT: { bg: "#3b82f6", light: "#dbeafe" },
    PROMETHEE: { bg: "#8b5cf6", light: "#ede9fe" },
    TOPSIS: { bg: "#10b981", light: "#d1fae5" },
    VIKOR: { bg: "#f59e0b", light: "#fef3c7" },
    COPRAS: { bg: "#ef4444", light: "#fee2e2" },
  };

  // Render correlation matrix
  const renderCorrelationMatrix = () => {
    if (!comparisonResult?.correlations) return null;

    const methods = ["MAUT", "PROMETHEE", "TOPSIS", "VIKOR", "COPRAS"];
    const correlations = comparisonResult.correlations;

    // Build matrix
    const getCorrelation = (m1, m2) => {
      if (m1 === m2) return 1.0;
      const key1 = `${m1}_vs_${m2}`;
      const key2 = `${m2}_vs_${m1}`;
      return correlations[key1] ?? correlations[key2] ?? 0;
    };

    const getCorrelationColor = (value) => {
      if (value >= 0.9) return "#059669";
      if (value >= 0.7) return "#10b981";
      if (value >= 0.5) return "#f59e0b";
      if (value >= 0.3) return "#f97316";
      return "#ef4444";
    };

    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85em" }}>
          <thead>
            <tr>
              <th style={{ padding: "8px", background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                Spearman rho
              </th>
              {methods.map((m) => (
                <th
                  key={m}
                  style={{
                    padding: "8px",
                    background: methodColors[m].light,
                    border: "1px solid #e2e8f0",
                    fontWeight: "600",
                  }}
                >
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {methods.map((row) => (
              <tr key={row}>
                <td
                  style={{
                    padding: "8px",
                    background: methodColors[row].light,
                    border: "1px solid #e2e8f0",
                    fontWeight: "600",
                  }}
                >
                  {row}
                </td>
                {methods.map((col) => {
                  const value = getCorrelation(row, col);
                  return (
                    <td
                      key={col}
                      style={{
                        padding: "8px",
                        border: "1px solid #e2e8f0",
                        textAlign: "center",
                        background: row === col ? "#f1f5f9" : "#fff",
                        color: row === col ? "#64748b" : getCorrelationColor(value),
                        fontWeight: row === col ? "400" : "600",
                      }}
                    >
                      {value.toFixed(4)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render rankings comparison table
  const renderRankingsTable = () => {
    if (!comparisonResult?.rankings) return null;

    const methods = ["MAUT", "PROMETHEE", "TOPSIS", "VIKOR", "COPRAS"];
    const rankings = comparisonResult.rankings;

    // Get all tools from any method
    const allTools = rankings.MAUT?.map((r) => ({ id: r.toolId, name: r.toolName })) || [];

    // Create rank lookup for each method
    const getRank = (method, toolId) => {
      const ranking = rankings[method];
      if (!ranking) return "-";
      const idx = ranking.findIndex((r) => r.toolId === toolId);
      return idx >= 0 ? idx + 1 : "-";
    };

    const getScore = (method, toolId) => {
      const ranking = rankings[method];
      if (!ranking) return "-";
      const item = ranking.find((r) => r.toolId === toolId);
      return item ? item.score.toFixed(4) : "-";
    };

    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85em" }}>
          <thead>
            <tr>
              <th
                style={{
                  padding: "10px",
                  background: "#1e293b",
                  color: "#fff",
                  border: "1px solid #334155",
                  textAlign: "left",
                }}
              >
                Tool Name
              </th>
              {methods.map((m) => (
                <th
                  key={m}
                  colSpan={2}
                  style={{
                    padding: "10px",
                    background: methodColors[m].bg,
                    color: "#fff",
                    border: "1px solid #334155",
                    textAlign: "center",
                  }}
                >
                  {m}
                </th>
              ))}
            </tr>
            <tr>
              <th style={{ padding: "6px", background: "#f8fafc", border: "1px solid #e2e8f0" }}></th>
              {methods.map((m) => (
                <React.Fragment key={m}>
                  <th
                    style={{
                      padding: "6px",
                      background: methodColors[m].light,
                      border: "1px solid #e2e8f0",
                      fontSize: "0.75em",
                    }}
                  >
                    Rank
                  </th>
                  <th
                    style={{
                      padding: "6px",
                      background: methodColors[m].light,
                      border: "1px solid #e2e8f0",
                      fontSize: "0.75em",
                    }}
                  >
                    Score
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {allTools.map((tool) => (
              <tr key={tool.id}>
                <td
                  style={{
                    padding: "8px",
                    border: "1px solid #e2e8f0",
                    fontWeight: "500",
                    background: "#fff",
                  }}
                >
                  {tool.name}
                </td>
                {methods.map((m) => {
                  const rank = getRank(m, tool.id);
                  const isTop3 = typeof rank === "number" && rank <= 3;
                  return (
                    <React.Fragment key={m}>
                      <td
                        style={{
                          padding: "8px",
                          border: "1px solid #e2e8f0",
                          textAlign: "center",
                          fontWeight: isTop3 ? "700" : "400",
                          background: isTop3 ? methodColors[m].light : "#fff",
                          color: isTop3 ? methodColors[m].bg : "#374151",
                        }}
                      >
                        {rank === 1 && "🥇 "}
                        {rank === 2 && "🥈 "}
                        {rank === 3 && "🥉 "}
                        {rank}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          border: "1px solid #e2e8f0",
                          textAlign: "center",
                          fontSize: "0.85em",
                          color: "#64748b",
                        }}
                      >
                        {getScore(m, tool.id)}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render Top-K agreement
  const renderTopKAgreement = () => {
    if (!comparisonResult?.topKAgreement) return null;

    const agreement = comparisonResult.topKAgreement;

    return (
      <div
        style={{
          background: "#f8fafc",
          borderRadius: "8px",
          padding: "16px",
          border: "1px solid #e2e8f0",
        }}
      >
        <h5 style={{ margin: "0 0 12px 0", color: "#1e293b", fontSize: "0.95em" }}>
          Top-{agreement.k} Agreement Analysis
        </h5>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "12px",
              borderRadius: "6px",
              border: "1px solid #e2e8f0",
            }}
          >
            <div style={{ fontSize: "0.75em", color: "#64748b", marginBottom: "4px" }}>
              All Methods Agree On
            </div>
            <div style={{ fontSize: "1.5em", fontWeight: "700", color: "#059669" }}>
              {agreement.allMethodsAgreementPercentage}%
            </div>
            <div style={{ fontSize: "0.7em", color: "#94a3b8" }}>
              {agreement.allMethodsAgreeOn?.length || 0} of {agreement.k} tools
            </div>
          </div>

          <div
            style={{
              background: "#fff",
              padding: "12px",
              borderRadius: "6px",
              border: "1px solid #e2e8f0",
            }}
          >
            <div style={{ fontSize: "0.75em", color: "#64748b", marginBottom: "4px" }}>
              Viable Tools Analyzed
            </div>
            <div style={{ fontSize: "1.5em", fontWeight: "700", color: "#3b82f6" }}>
              {comparisonResult.viableToolCount}
            </div>
            <div style={{ fontSize: "0.7em", color: "#94a3b8" }}>
              passed mandatory criteria
            </div>
          </div>
        </div>

        <div style={{ fontSize: "0.8em", color: "#475569" }}>
          <strong>Pairwise Top-{agreement.k} Agreement:</strong>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              marginTop: "8px",
            }}
          >
            {Object.entries(agreement.pairwiseAgreement || {}).map(([pair, pct]) => (
              <span
                key={pair}
                style={{
                  background: pct >= 66 ? "#d1fae5" : pct >= 33 ? "#fef3c7" : "#fee2e2",
                  color: pct >= 66 ? "#065f46" : pct >= 33 ? "#92400e" : "#991b1b",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "0.85em",
                }}
              >
                {pair.replace(/_vs_/g, " vs ")}: {pct}%
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <FormLayoutSelection title={t("methodComparisonTitle")}>
      {/* Header Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
          borderRadius: "12px 12px 0 0",
          padding: "16px 20px",
          color: "#fff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <ScienceIcon sx={{ fontSize: 24 }} />
          <div>
            <h4 style={{ margin: 0, fontSize: "1.1em", fontWeight: "600" }}>
              {t("researchMethodComparison")}
            </h4>
            <span style={{ fontSize: "0.8em", opacity: 0.9 }}>
              {t("compareAllMCDMMethods")}
            </span>
          </div>
        </div>
      </div>

      {/* Dropdown Selection Section - Outside the colored header */}
      <div
        style={{
          background: "#f8fafc",
          borderRadius: "0 0 12px 12px",
          padding: "20px",
          marginBottom: "1.5rem",
          border: "1px solid #e2e8f0",
          borderTop: "none",
        }}
      >
        <div className="row">
          <FormSelectSelection
            label={t("panel")}
            options={panelOptions}
            value={selectedPanel}
            onChange={handlePanelChange}
          />
          <FormSelectSelection
            label={t("category")}
            options={categoryOptions}
            value={selectedCategory}
            onChange={handleCategoryChange}
          />
          <FormSelectSelection
            label={t("subcategory")}
            options={subcategoryOptions}
            value={selectedSubcategory}
            onChange={handleSubcategoryChange}
          />
        </div>
      </div>

      {/* Criteria Selection */}
      {loadingCriteria ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            background: "#f8fafc",
            borderRadius: "8px",
            padding: "2rem",
          }}
        >
          <LinearProgress sx={{ width: "50%", mb: 2 }} />
          <p style={{ color: "#64748b" }}>{t("loading")}</p>
        </div>
      ) : (
        selectedSubcategory && (
          <>
            {/* Criteria groups - simplified version */}
            {criteriaW1.length > 0 && (
              <div style={{ marginBottom: "1.5rem" }}>
                <h5 style={{ color: "#dc2626", marginBottom: "8px" }}>
                  {t("toolSelectionTitleStep2")}
                </h5>
                {criteriaW1.map((crit) => (
                  <CriterionValueGroup
                    key={crit.id}
                    criterion={crit}
                    selectedValueIds={selectedCVIdsW1}
                    setSelectedValueIds={setSelectedCVIdsW1}
                  />
                ))}
              </div>
            )}

            {criteriaW05.length > 0 && (
              <div style={{ marginBottom: "1.5rem" }}>
                <h5 style={{ color: "#d97706", marginBottom: "8px" }}>
                  {t("toolSelectionTitleStep3")}
                </h5>
                {criteriaW05.map((crit) => (
                  <CriterionValueGroup
                    key={crit.id}
                    criterion={crit}
                    selectedValueIds={selectedCVIdsW05}
                    setSelectedValueIds={setSelectedCVIdsW05}
                  />
                ))}
              </div>
            )}

            {criteriaW033.length > 0 && (
              <div style={{ marginBottom: "1.5rem" }}>
                <h5 style={{ color: "#059669", marginBottom: "8px" }}>
                  {t("toolSelectionTitleStep4")}
                </h5>
                {criteriaW033.map((crit) => (
                  <CriterionValueGroup
                    key={crit.id}
                    criterion={crit}
                    selectedValueIds={selectedCVIdsW033}
                    setSelectedValueIds={setSelectedCVIdsW033}
                  />
                ))}
              </div>
            )}

            {/* Compare Button */}
            <div style={{ textAlign: "center", marginBottom: "2rem" }}>
              <button
                style={{
                  padding: "12px 32px",
                  fontSize: "1em",
                  fontWeight: "600",
                  background: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                  boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)",
                }}
                onClick={handleCompare}
                disabled={loadingComparison}
              >
                <CompareArrowsIcon />
                {loadingComparison ? t("loading") : t("compareAllMethods")}
              </button>
            </div>
          </>
        )
      )}

      {/* Results Section */}
      {loadingComparison && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            background: "#f8fafc",
            borderRadius: "8px",
            padding: "2rem",
          }}
        >
          <LinearProgress sx={{ width: "50%", mb: 2 }} />
          <p style={{ color: "#64748b" }}>{t("comparingMethods")}</p>
        </div>
      )}

      {hasCompared && comparisonResult && (
        <div>
          {/* Top-K Agreement */}
          <div style={{ marginBottom: "2rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <EmojiEventsIcon sx={{ color: "#f59e0b", fontSize: 22 }} />
              <h4 style={{ margin: 0, color: "#1e293b", fontSize: "1em" }}>
                {t("agreementAnalysis")}
              </h4>
            </div>
            {renderTopKAgreement()}
          </div>

          {/* Correlation Matrix */}
          <div style={{ marginBottom: "2rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <BarChartIcon sx={{ color: "#3b82f6", fontSize: 22 }} />
              <h4 style={{ margin: 0, color: "#1e293b", fontSize: "1em" }}>
                {t("spearmanCorrelationMatrix")}
              </h4>
              <Tooltip
                title={
                  <div style={{ padding: "8px" }}>
                    <strong>Spearman Correlation</strong> measures rank correlation between methods.
                    <br />
                    Values close to 1.0 indicate high agreement in ranking order.
                    <br />
                    Values close to 0 indicate no correlation.
                  </div>
                }
                arrow
              >
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8", cursor: "help" }} />
              </Tooltip>
            </div>
            {renderCorrelationMatrix()}
          </div>

          {/* Rankings Table */}
          <div style={{ marginBottom: "2rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <TableChartIcon sx={{ color: "#8b5cf6", fontSize: 22 }} />
              <h4 style={{ margin: 0, color: "#1e293b", fontSize: "1em" }}>
                {t("rankingsComparison")}
              </h4>
            </div>
            {renderRankingsTable()}
          </div>

          {/* Export Data Section */}
          <div
            style={{
              background: "#f8fafc",
              borderRadius: "8px",
              padding: "16px",
              border: "1px solid #e2e8f0",
              marginTop: "1rem",
            }}
          >
            <h5 style={{ margin: "0 0 8px 0", color: "#475569", fontSize: "0.9em" }}>
              {t("exportForResearch")}
            </h5>
            <p style={{ fontSize: "0.8em", color: "#64748b", margin: 0 }}>
              {t("exportInstructions")}
            </p>
            <button
              style={{
                marginTop: "12px",
                padding: "8px 16px",
                fontSize: "0.85em",
                background: "#fff",
                color: "#3b82f6",
                border: "1px solid #3b82f6",
                borderRadius: "6px",
                cursor: "pointer",
              }}
              onClick={() => {
                const dataStr = JSON.stringify(comparisonResult, null, 2);
                const dataBlob = new Blob([dataStr], { type: "application/json" });
                const url = URL.createObjectURL(dataBlob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `mcdm_comparison_${new Date().toISOString().split("T")[0]}.json`;
                link.click();
              }}
            >
              {t("downloadJSON")}
            </button>
          </div>
        </div>
      )}
    </FormLayoutSelection>
  );
};

export default observer(MethodComparison);
